import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Clock, X, Check } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';

interface SystemMessageCardProps {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  className?: string;
  onDismiss?: (id: string) => void;
  isRead?: boolean;
  onMarkAsRead?: (id: string) => void;
}

const SystemMessageCard: React.FC<SystemMessageCardProps> = ({
  id,
  title,
  body,
  createdAt,
  className = "",
  onDismiss,
  isRead = false,
  onMarkAsRead
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [localIsRead, setLocalIsRead] = useState(isRead);

  const shouldShowExpandButton = body.length > 150;
  const displayBody = isExpanded ? body : body.slice(0, 150) + (shouldShowExpandButton ? '...' : '');

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        notify.error('User not authenticated');
        setIsDismissing(false);
        return;
      }

      const { error } = await supabase
        .from('system_message_dismissals')
        .insert({
          user_id: user.id,
          message_id: id
        });

      if (error) {
        console.error('Error dismissing message:', error);
        notify.error('Failed to dismiss message');
        setIsDismissing(false);
        return;
      }

      // Call the onDismiss callback if provided
      if (onDismiss) {
        onDismiss(id);
      }
    } catch (err) {
      console.error('Unexpected error dismissing message:', err);
      notify.error('Failed to dismiss message');
      setIsDismissing(false);
    }
  };

  const handleMarkAsRead = async () => {
    setIsMarking(true);
    try {
      // Store read status in localStorage
      const readMessages = JSON.parse(localStorage.getItem('readSystemMessages') || '[]');
      if (!readMessages.includes(id)) {
        readMessages.push(id);
        localStorage.setItem('readSystemMessages', JSON.stringify(readMessages));
      }

      setLocalIsRead(true);
      if (onMarkAsRead) {
        onMarkAsRead(id);
      }
    } catch (err) {
      console.error('Unexpected error marking message as read:', err);
      notify.error('Failed to mark message as read');
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <Card className={`transition-all duration-200 hover:shadow-md w-full ${localIsRead ? 'bg-gray-50' : 'bg-blue-50'} ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            <CardTitle className="text-lg font-semibold text-foreground break-words flex-1">{title}</CardTitle>
            {localIsRead && (
              <Badge variant="secondary" className="flex-shrink-0">
                <Check className="w-3 h-3 mr-1" />
                Read
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center text-muted-foreground text-sm">
              <Clock className="w-4 h-4 mr-1" />
              {format(new Date(createdAt), 'MMM dd, HH:mm')}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              disabled={isDismissing}
              className="h-8 w-8 p-0 hover:bg-muted"
              title="Dismiss message"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap break-words">
          {displayBody}
        </p>
        <div className="flex items-center gap-2 mt-3">
          {shouldShowExpandButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-0 h-auto text-primary hover:text-primary/80"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  {t('dashboard:collapse')}
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  {t('dashboard:expand')}
                </>
              )}
            </Button>
          )}
          {!localIsRead && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAsRead}
              disabled={isMarking}
              className="ml-auto text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <Check className="w-4 h-4 mr-1" />
              Mark as Read
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemMessageCard;